/**
 * RepBuddy - Profile Service
 * Loads and saves the seller profile via the authenticated backend API.
 * Falls back gracefully when Supabase / the backend is unavailable.
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Auth helper (shared pattern with hunter.js) ──────────────────────────────

async function authedHeaders() {
  const base = { 'Content-Type': 'application/json' };
  if (!supabase) return base;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return base;
    return { ...base, Authorization: `Bearer ${session.access_token}` };
  } catch {
    return base;
  }
}

// ─── GET /api/profile - returns the profile object or null ───────────────────

export async function fetchProfile() {
  try {
    const headers = await authedHeaders();
    // No auth token → skip the round-trip, rely on localStorage
    if (!headers.Authorization) return null;

    const r = await fetch(`${BACKEND_URL}/api/profile`, {
      method:  'GET',
      headers,
    });

    if (!r.ok) return null;

    const { profile } = await r.json();
    return profile || null;
  } catch {
    return null;
  }
}

// ─── POST /api/profile - upserts the profile, returns true on success ─────────

export async function saveProfileToAPI(profile) {
  try {
    const headers = await authedHeaders();
    if (!headers.Authorization) return false; // no session - skip

    const r = await fetch(`${BACKEND_URL}/api/profile`, {
      method:  'POST',
      headers,
      body:    JSON.stringify({ profile }),
    });

    if (!r.ok) return false;

    const { saved } = await r.json();
    return !!saved;
  } catch {
    return false;
  }
}

// ─── Auto-generate value prop + proof points ─────────────────────────────────
// Both return { valueProp, proofPoints, source, ... } on success. They throw on failure so the
// caller can surface a real message (bad auth, rate limit, timeout) rather than silently no-op -
// this is a button the user pressed, not a background sync.

async function postJson(path, body) {
  const headers = await authedHeaders();
  if (!headers.Authorization) throw new Error('Please sign in to use this.');

  const r = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers,
    body:   JSON.stringify(body),
  });

  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

// Draft from the seller's company website (hybrid: real pages first, grounded search fallback).
export function generateFromWebsite(website, company = '') {
  return postJson('/api/seller/generate-from-web', { website, company });
}

// Draft from text the client already extracted (e.g. a case-study PDF parsed in the browser).
export function generateFromText(sourceText, company = '') {
  return postJson('/api/seller/generate-from-text', { sourceText, company });
}
